import { selectData } from "fe-shared/utils/reducerUtils";
import { fromJS } from "immutable";

import {
  bot,
  messageFrom,
  messageStatus,
  messageType,
  messageTypes,
} from "constants";
import { decodeString, encodeString } from "utils";
import { checkStorageAvailability } from "utils/storage";
import {
  STATE_WEBBOT_ACTIONS,
  STATE_WEBBOT_ALL_MESSAGES,
  STATE_WEBBOT_CONNECTING,
  STATE_WEBBOT_CONNECTION,
  STATE_WEBBOT_FROM,
  STATE_WEBBOT_MESSAGE,
  STATE_WEBBOT_MESSAGES,
  STATE_WEBBOT_PREVIEWS,
  STATE_WEBBOT_STATUS,
  STATE_WEBBOT_TYPE,
} from "./default";

export const isFromHuman = from => from === messageFrom.human;
export const isChoiceType = type => type === messageTypes.choice;
export const isActionTypeWait = action =>
  selectData(action, STATE_WEBBOT_TYPE) === messageType.wait;
export const isActionPending = action =>
  selectData(action, STATE_WEBBOT_STATUS) === messageStatus.pending;
export const isMessageFromBot = message =>
  message.getIn([STATE_WEBBOT_MESSAGE, STATE_WEBBOT_FROM], false) === bot;
export const parseNumberToString = value => {
  if (value && Number.isInteger(value)) {
    return String(value);
  }
  return value;
};

export const addPreview = (state, { payload }) => {
  const preview = fromJS(payload);
  return state.update(STATE_WEBBOT_PREVIEWS, previews =>
    previews.push(preview)
  );
};

export const setPreviousMessages = (state, { payload }) => {
  const { botId, webchatKey } = payload;
  let allMessages = localStorage.getItem(
    `${encodeString(`${STATE_WEBBOT_ALL_MESSAGES}${botId}-${webchatKey}`)}`
  );
  allMessages = fromJS(JSON.parse(decodeString(allMessages)));
  return state.set(STATE_WEBBOT_MESSAGES, allMessages);
};

export const hidePendingMessages = state =>
  state.update(STATE_WEBBOT_MESSAGES, messages =>
    messages.map(message =>
      message.updateIn(
        [STATE_WEBBOT_MESSAGE, STATE_WEBBOT_ACTIONS],
        actions =>
          actions &&
          actions.map(action =>
            action.update(STATE_WEBBOT_STATUS, status =>
              status === messageStatus.pending ? messageStatus.skip : status
            )
          )
      )
    )
  );

export const newMessageReceived = (state, { payload }) => {
  payload.message.actions.forEach(action => {
    if (action.type !== "wait" && !(action.type === "text" && !action.text))
      window.top.postMessage(
        {
          message: "on-message-added",
          from: payload.message.from,
          action,
        },
        "*"
      );
  });

  let message = fromJS(payload);
  message = message.updateIn(
    [STATE_WEBBOT_MESSAGE, STATE_WEBBOT_ACTIONS],
    actions =>
      actions &&
      actions.map(action =>
        action.set(
          STATE_WEBBOT_STATUS,
          isFromHuman(payload.message.from)
            ? messageStatus.visible
            : messageStatus.pending
        )
      )
  );
  message = message.delete("botId");
  const messageToLocalStorage = message.updateIn(
    [STATE_WEBBOT_MESSAGE, STATE_WEBBOT_ACTIONS],
    actions =>
      actions &&
      actions.map(action => {
        const type = selectData(action, STATE_WEBBOT_TYPE);
        if (type === messageType.wait) {
          return action.set(STATE_WEBBOT_STATUS, messageStatus.skip);
        }
        return action.set(STATE_WEBBOT_STATUS, messageStatus.visible);
      })
  );

  return state
    .update(STATE_WEBBOT_MESSAGES, messages => {
      const { botId, webchatKey } = payload;

      const localStorageAvailability = checkStorageAvailability("localStorage");
      const isClearPastConversation = localStorage.getItem("isClearPastConversation")

      try {
        if (!isClearPastConversation) {
          let allMessagesSavedInLocalStorage;

          if (localStorageAvailability) {
            allMessagesSavedInLocalStorage = localStorage.getItem(
              `${encodeString(
                `${STATE_WEBBOT_ALL_MESSAGES}${botId}-${webchatKey}`
              )}`
            );
          }

          if (allMessagesSavedInLocalStorage) {
            allMessagesSavedInLocalStorage = JSON.parse(
              decodeString(allMessagesSavedInLocalStorage)
            );
          } else {
            allMessagesSavedInLocalStorage = [];
          }

          const allMessages = allMessagesSavedInLocalStorage.concat(
            messageToLocalStorage.toJS()
          );

          if (localStorageAvailability) {
            localStorage.setItem(
              `${encodeString(
                `${STATE_WEBBOT_ALL_MESSAGES}${botId}-${webchatKey}`
              )}`,
              encodeString(JSON.stringify(allMessages))
            );
          }
        }
      }
      catch (e) {}

      return messages.push(message);
    })
    .set(STATE_WEBBOT_CONNECTION, true)
    .set(STATE_WEBBOT_CONNECTING, false);
};

export const messageSentToSdk = (state, { payload }) => {
  window.top.postMessage(
    {
      message: "on-message-before-display",
      payload,
    },
    "*"
  );

  return state;
};

export const hideWaits = state =>
  state.update(STATE_WEBBOT_MESSAGES, messages =>
    messages.map(message =>
      message.updateIn(
        [STATE_WEBBOT_MESSAGE, STATE_WEBBOT_ACTIONS],
        actions =>
          actions &&
          actions.map(action =>
            action.update(STATE_WEBBOT_STATUS, status =>
              selectData(action, STATE_WEBBOT_TYPE) === messageType.wait
                ? messageStatus.skip
                : status
            )
          )
      )
    )
  );

export const showMessagesBeforeWait = state => {
  let isPendingWaitDetected = false;
  return state.update(STATE_WEBBOT_MESSAGES, messages =>
    messages.map(message =>
      message.updateIn(
        [STATE_WEBBOT_MESSAGE, STATE_WEBBOT_ACTIONS],
        actions =>
          actions &&
          actions.map(action => {
            if (isActionPending(action) && !isPendingWaitDetected) {
              if (isActionTypeWait(action)) {
                isPendingWaitDetected = true;
                return action.set(STATE_WEBBOT_STATUS, messageStatus.skip);
              }
              return action.set(STATE_WEBBOT_STATUS, messageStatus.visible);
            }
            return action;
          })
      )
    )
  );
};
